import { useCallback, useEffect, useRef } from 'react';
import { isOwnBlockEcho, readDocumentBlocks } from '../../extensions/bundled/documents/documentsStore';
import { BLOCK_RECORD_TYPE } from '../../extensions/bundled/documents/docBlocks';

// Same quiet window as the docbody reconcile (useDocumentCollab.js): while
// someone types here or live edits arrive, a saved block is older than what
// this screen shows — it waits.
const QUIET_MS = 1500;
const RETRY_MS = 1600;

/** The block id of a block record change, or null if it is not one of `documentId`'s. */
export function blockIdOf(record, documentId) {
  if (!record) return null;
  if (record.record_type === BLOCK_RECORD_TYPE && record.scope_id === documentId && typeof record.bid === 'string') return record.bid;
  // A delete arrives without data (sync.ex nils it): only its id says whose it is.
  const prefix = `${documentId}::b:`;
  if (record.deleted && typeof record.id === 'string' && record.id.startsWith(prefix)) return record.id.slice(prefix.length);
  return null;
}

const byOrder = (a, b) => (a.key !== b.key ? ((a.key || '') < (b.key || '') ? -1 : 1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * The open document's safety net for block records (docBlocks.js) that a
 * teammate's save sends: live edits normally got there first, so this is
 * usually a no-op; it repairs a screen that missed one (a dropped socket).
 *
 * Only the blocks the records name are touched (`applyFullBlocks` onlyIds):
 * the stored order only places them, so a line this screen has and the store
 * does not yet (a teammate's paragraph applied live, its save still on the
 * way) is left alone. A record this device wrote itself is skipped — the
 * broadcast can hand an older save back after a newer local one.
 */
export function useDocumentBlockReconcile(session, { documentId, scope, applyRemote }) {
  const { editorRef, bodyTimer, bodySaveInFlightRef, lastLocalEditAtRef, lastOpsAppliedAtRef, placeNextRemoteRef } = session;
  const pendingRef = useRef(new Map()); // bid -> { deleted, node, key }
  const timerRef = useRef(null);
  const flushRef = useRef(null);

  const schedule = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flushRef.current?.();
    }, RETRY_MS);
  }, []);

  flushRef.current = async () => {
    if (!pendingRef.current.size) return;
    const now = Date.now();
    const busy =
      bodyTimer.current ||
      bodySaveInFlightRef.current ||
      now - lastLocalEditAtRef.current < QUIET_MS ||
      now - lastOpsAppliedAtRef.current < QUIET_MS;
    if (busy) {
      schedule();
      return;
    }
    const pending = pendingRef.current;
    pendingRef.current = new Map();
    let stored;
    try {
      stored = await readDocumentBlocks(documentId, scope);
    } catch {
      return;
    }
    const entries = new Map(stored.snapshot.blocks.map((b) => [b.id, { id: b.id, node: b.node, key: stored.keys.get(b.id) }]));
    for (const [bid, change] of pending) {
      if (change.deleted) entries.delete(bid);
      else entries.set(bid, { id: bid, node: change.node, key: change.key ?? entries.get(bid)?.key ?? null });
    }
    const blocks = [...entries.values()].sort(byOrder).map(({ id, node }) => ({ id, node }));
    const onlyIds = new Set(pending.keys());
    // These come with their stored places: a teammate's drag is taken as theirs.
    if (placeNextRemoteRef) placeNextRemoteRef.current = true;
    applyRemote(() => editorRef.current?.applyFullBlocks({ version: 1, blocks }, { onlyIds }));
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      pendingRef.current = new Map();
    },
    [documentId]
  );

  /** Takes the records of one change batch; returns true if any was a block of this document. */
  return useCallback(
    (records) => {
      let any = false;
      for (const r of records) {
        const bid = blockIdOf(r, documentId);
        if (bid == null) continue;
        any = true;
        if (!r.deleted && isOwnBlockEcho(r.id, r.node)) continue;
        pendingRef.current.set(bid, r.deleted ? { deleted: true } : { node: r.node, key: r.sort_key ?? null });
      }
      if (pendingRef.current.size) flushRef.current?.();
      return any;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, scope.type, scope.teamId]
  );
}
